<template>
  <div>
    <v-row class="user-row">
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="First name"
          class="contact-label"
        />
        <sn-text-field
          v-model="firstName"
          placeholder="First name"
          data-cy="first-name-field"
          required
          maxlength="50"
          :rules="[limit50Chars, notEmpty]"
          @keyup="handleChanges"
        />
      </v-flex>
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Last name"
          class="contact-label"
        />
        <sn-text-field
          v-model="lastName"
          placeholder="Last name"
          data-cy="last-name-field"
          required
          maxlength="50"
          :rules="[limit50Chars, notEmpty]"
          @keyup="handleChanges"
        />
      </v-flex>
    </v-row>
    <v-row class="user-row">
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Email"
          class="contact-label"
        />
        <sn-text-field
          v-if="!edit"
          v-model="email"
          data-cy="email-field"
          :error-messages="emailErrors"
          placeholder="Enter email"
          required
          :rules="[validateEmail, notEmpty]"
          @blur="getExistingUser"
          @keyup="handleChanges"
        />
        <sn-text-field
          v-else
          :value="email"
          :disabled="true"
        />
      </v-flex>
      <v-flex
        class="md6 sm12 xs12 user-column"
      >
        <sn-label
          title="Account type"
          class="contact-label"
        />
        <sn-combobox
          v-model="roleType"
          data-cy="account-type-dropdown"
          :items="companyRoleTypes"
          :placeholder="edit ? '' : 'Select a role for this user'"
          :disabled="edit ? !managesUsers : false"
          attach
          @search-input="handleSearchInput($event, 'role')"
          @change="environmentSettingOnChange"
        />
      </v-flex>
    </v-row>
    <v-row class="user-row">
      <v-flex
        class="md6 sm12 xs12 user-column"
      >
        <sn-label
          title="Time zone"
          class="contact-label"
        />
        <sn-combobox
          v-model="timeZone"
          data-cy="timezone-dropdown"
          :items="timeZoneOptions"
          :placeholder="edit ? '' : 'Select a preferred time zone for this user'"
          attach
          @search-input="handleSearchInput($event, 'timezone')"
          @change="handleChanges"
        />
      </v-flex>
      <v-flex class="md6 sm12 xs12 user-column">
        <sn-label
          title="Environments"
          class="contact-label"
        />
        <sn-text-field
          :value="enabledEnvironments"
          :disabled="true"
        />
      </v-flex>
    </v-row>
    <v-row class="user-row">
      <v-flex
        v-if="(!edit && !isSnAdminCompany) || (allowedOrganizations != null && allowedOrganizations.length > 0)"
        class="md12 sm12 xs12"
      >
        <sn-label
          title="Organizations"
          class="contact-label"
        />
        <sr-organization-combobox
          v-model="organizations"
          class="orgs"
          data-cy="orgs-dropdown"
          :organizations="allowedOrganizations"
          :disabled="companiesDisabled"
          @input="handleChanges"
        />
      </v-flex>
    </v-row>
    <v-row
      v-if="edit"
      class="user-row"
    >
      <v-flex v-if="lastLoginAt == null" class="md12 mb-2">
        <sn-btn
          data-cy="resend-invite"
          text="Resend invite"
          type="text"
          size="small"
          :disabled="loading"
          @click="resendInvite"
        />
      </v-flex>
      <v-flex class="md12 mb-2">
        <sn-btn
          data-cy="password-reset"
          text="Require password reset"
          type="text"
          size="small"
          :disabled="passwordResetLoading"
          @click="showPasswordResetModal = true"
        />
      </v-flex>
    </v-row>
    <v-row
      v-if="edit"
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <div class="d-flex">
          <sn-btn
            style="marginTop: 10px"
            text="Save"
            type="primary"
            size="large"
            data-cy="personal-info-save-btn"
            :disabled="!hasUserInfoChanges"
            :loading="saveBtnLoading"
            @click="handleSave"
          />
        </div>
      </v-col>
    </v-row>
    <sn-modal
      v-model="showPasswordResetModal"
      type="decision"
      :title="`Require password reset?`"
      data-cy="confirmation-modal"
      primary-text="Yes"
      secondary-text="Cancel"
      :loading="passwordResetLoading"
      @submit="forcePasswordReset"
      @close="showPasswordResetModal=false"
    >
      <span>This action will require the user to reset their password.</span>
    </sn-modal>
  </div>
</template>

<script>

import srOrganizationCombobox from '../srui/srOrganizationCombobox'
import snSigningRoomService, { TIMEZONES } from '../../services/snSigningRoomService'
import { mapGetters, mapMutations } from 'vuex'
import { user, editUser, addUser, updateUsersForcePasswordReset, companyName } from '../graphql/signing_room_queries.gql'
import { isEqual } from 'lodash'

export default {
  name: 'SrUserInfo',
  components: {
    srOrganizationCombobox
  },
  props: {
    edit: {
      type: Boolean,
      required: true,
      default: false
    },
    user: {
      type: Object,
      required: false,
      default: () => {}
    },
    roleEnvironments: {
      type: Object,
      required: false,
      default: () => {}
    },
    roleTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    orgs: {
      type: Array,
      required: false,
      default: () => []
    },
    userGuid: {
      type: String,
      default: '',
      required: false
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      firstName: '',
      dbFirstName: '',
      lastName: '',
      dbLastName: '',
      timeZone: '',
      dbTimeZone: '',
      email: '',
      dbEmail: '',
      emailErrors: [],
      emailAlreadyInUse: false,
      dbEmailAlreadyInUse: false,
      roleType: '',
      dbRoleType: '',
      companyRoleTypes: [],
      timeZoneOptions: TIMEZONES.map(tz => tz.label),
      enabledEnvironments: [],
      dbEnabledEnvironments: [],
      organizations: [],
      dbOrganizations: [],
      allowedOrganizations: [],
      validTimeZone: true,
      validRoleType: true,
      userInfoToChange: [],
      lastQueriedEmail: '',
      loading: false,
      saveBtnLoading: false,
      passwordResetLoading: false,
      showPasswordResetModal: false,
      companyName: ''
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'user',
      orgId: 'orgId'
    }),
    hasUserInfoChanges () {
      return !!this.firstName &&
        !!this.lastName &&
        !!this.timeZone &&
        !!this.roleType &&
        !!this.email &&
        !this.emailAlreadyInUse &&
        this.enabledEnvironments.length > 0 &&
        (this.isSnAdminCompany || this.organizations?.length > 0) &&
        this.validTimeZone &&
        this.validRoleType &&
        this.validateEmail(this.email) === true &&
        this.limit50Chars(this.firstName) === true &&
        this.limit50Chars(this.lastName) === true &&
        !isEqual(this.dbValues, this.currentValues)
    },
    limit50Chars () {
      return (input) => input?.length >= 50 ? '50 character limit' : true
    },
    notEmpty () {
      return (input) => input?.length > 0 ? true : 'Required field'
    },
    validateEmail () {
      return (input) => input ? snSigningRoomService.validateEmail(input) ? true : 'Invalid email format' : false
    },
    managesUsers () {
      return snSigningRoomService.hasPermission(this.loggedInUser, 'manageUsers')
    },
    isEditingSelf () {
      return this.loggedInUser.guid === this.userGuid
    },
    companiesDisabled () {
      return !this.managesUsers || this.isEditingSelf
    },
    dbValues () {
      return { firstName: this.dbFirstName, lastName: this.dbLastName, timeZone: this.dbTimeZone, email: this.dbEmail, organizations: this.dbOrganizations.length ? JSON.stringify(this.dbOrganizations) : this.dbOrganizations, roleType: this.dbRoleType, enabledEnvironments: this.dbEnabledEnvironments }
    },
    currentValues () {
      return { firstName: this.firstName, lastName: this.lastName, timeZone: this.timeZone, email: this.email, organizations: this.organizations.length ? JSON.stringify(this.organizations) : this.organizations, roleType: this.roleType, enabledEnvironments: this.enabledEnvironments }
    },
    lastLoginAt () { return this.user?.lastLoginAt },
    isSnAdminCompany () { return this.user?.company?.name === 'SnAdmin Company' || this.companyName === 'SnAdmin Company' }
  },
  watch: {
    user () {
      // update any user role info that changes when permissions are changed
      this.setEditUserInfo(this.user)
      this.environmentSettingOnChange()
    }
  },
  created () {
    if (!this.edit && this.roleTypes.length > 0) {
      this.roleType = this.roleTypes[0]
    }
    this.companyRoleTypes = this.roleTypes
    this.allowedOrganizations = this.orgs
    this.setEditUserInfo(this.user)
    this.environmentSettingOnChange()
    this.getCompanyName()
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'resetAppTimeout']),
    setEditUserInfo (user) {
      if (this.edit) {
        this.firstName = user?.firstName
        this.lastName = user?.lastName
        this.timeZone = user?.timezone
        this.roleType = user?.roleType
        this.companyRoleTypes = user?.companyRoleTypes
        this.email = user?.email
        this.allowedOrganizations = user?.allowedOrgsFormatted ? JSON.parse(user?.allowedOrgsFormatted) : []
        this.organizations = user?.selectedOrgsFormatted?.length ? JSON.parse(user?.selectedOrgsFormatted) : []
        this.dbFirstName = user?.firstName
        this.dbLastName = user?.lastName
        this.dbTimeZone = user?.timezone
        this.dbEmail = user?.email
        this.dbOrganizations = user?.selectedOrgsFormatted?.length ? JSON.parse(user?.selectedOrgsFormatted) : []
        this.dbRoleType = user?.roleType
      }
    },
    async handleChanges () {
      if (this.validateEmail(this.email) === true && !this.edit) {
        await this.getExistingUser()
      }

      if (this.hasUserInfoChanges) {
        this.$emit('change', this.currentValues)
      } else {
        this.$emit('change', {})
      }
    },
    handleSearchInput (value, type) {
      switch (type) {
        case 'timezone':
          this.validTimeZone = this.timeZoneOptions.includes(value)
          break
        case 'role':
          this.validRoleType = this.companyRoleTypes.includes(value)
          break
      }
    },
    environmentSettingOnChange () {
      this.enabledEnvironments = this.roleEnvironments[this.roleType]
      this.dbEnabledEnvironments = this.roleEnvironments[this.roleType]
      this.handleChanges()
    },
    async getExistingUser () {
      if (this.email === this.lastQueriedEmail) {
        return
      }
      this.lastQueriedEmail = this.email

      const { data } = await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          email: this.email
        }
      })
      if (data.user) {
        this.emailErrors = ['Email already in use']
        this.emailAlreadyInUse = true
      } else {
        this.emailErrors = []
        this.emailAlreadyInUse = false
      }
    },
    getCompanyName () {
      this.$apollo.query({
        query: companyName,
        variables: {
          companyGuid: this.companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        this.companyName = data.company.name
      })
    },
    resendInvite () {
      this.loading = true
      const { firstName, lastName, email, timeZone, stagingEnabled, productionEnabled, roleType } = this.user
      this.$apollo.mutate({
        mutation: addUser,
        variables: {
          orgIds: [this.orgId],
          firstName,
          lastName,
          email,
          timeZone,
          stagingEnabled,
          productionEnabled,
          roleType,
          companyGuid: this.companyGuid,
          newInvite: false
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.addUser.error) {
          this.setErrorToastType({ description: data.addUser.error || 'There was an error with the request' })
        } else {
          this.setSuccessToast({ description: 'Invitation sent' })
        }
        this.resetAppTimeout()
      }).catch(err => {
        this.loading = false
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },
    handleSave () {
      this.saveBtnLoading = true
      let { firstName, lastName, timeZone, organizations, roleType, enabledEnvironments } = this.currentValues
      if (typeof organizations === 'string') {
        organizations = JSON.parse(organizations)
      }
      this.$apollo.mutate({
        mutation: editUser,
        variables: {
          guid: this.userGuid,
          orgIds: organizations.map(o => o.guid),
          firstName,
          lastName,
          timeZone,
          stagingEnabled: enabledEnvironments.includes('Staging'),
          productionEnabled: enabledEnvironments.includes('Production'),
          roleType
        }
      }).then(({ data }) => {
        if (data.editUser.response) {
          const reload = snSigningRoomService.checkForSelfReload([this.userGuid], this.loggedInUser)
          this.setSuccessToast({ description: `Updated user successfully${reload ? '. Applying...' : ''}` })
          this.$emit('save')
          if (!reload) {
            this.saveBtnLoading = false
          }
        } else {
          this.setErrorToastType({ description: data.editUser.error })
          this.saveBtnLoading = false
        }
      }).catch(err => {
        this.setErrorToastType({ description: err })
        this.saveBtnLoading = false
      })
    },
    forcePasswordReset () {
      this.passwordResetLoading = true
      this.$apollo
        .query({
          query: updateUsersForcePasswordReset,
          fetchPolicy: 'no-cache',
          variables: { guids: [this.userGuid] }
        })
        .then(({ data }) => {
          if (data.updateUsersForcePasswordReset?.response) {
            this.setSuccessToast({ description: 'Password reset has been initiated.' })
            this.showPasswordResetModal = false
          } else if (data.updateUsersForcePasswordReset?.error) {
            this.setErrorToastType({ description: data.updateUsersForcePasswordReset?.erro })
          }
        }).catch((error) => {
        this.setErrorToastType({ description: error.message })
      }).finally(() => this.passwordResetLoading = false)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.contact-label {
  padding-bottom: 8px;
}

.user-row {
  margin: 32px 0;

  &:first-child {
    margin: 10px 0 32px 0;
  }

  &:last-child {
    margin: 32px 0 10px 0;
  }

  &:nth-child(-n + 2){
    margin: 32px 0px;
  }

  &:last-child {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 20px;
    margin-top: 24px;
  }

  .user-column:first-child {
    padding-right: 32px;
  }

  .user-column:only-child {
    padding-left: 0;
  }
}

::v-deep .orgs .v-select__selections {
  padding: 8px 0 !important;
}

::v-deep .v-select__selections .v-select__selection--comma {
  padding-right: 5px !important;
}

@media (max-width: 959px) {
  .user-row {
    margin: 0;

    &:nth-child(-n + 2){
      margin: 0;
    }

    &:first-child {
      margin-top: 32px;
    }

    &:last-child {
      margin-top: 0;
    }

    .user-column {
      margin-bottom: 32px;
    }

    .user-column:first-child {
      padding-right: 0;
    }

    .user-column:last-child {
      padding-left: 0;
    }

    .user-column:only-child {
      padding-left: 0;
    }
  }
}
</style>
